<template>
	<div class="card p-0">

		<!-- Page header -->
		<div class="ms-header">
			<h1 class="ms-title">Manual Scheduling</h1>
			<p class="ms-subtitle">Create a confirmed visit directly for a participant and standardized patient.</p>
		</div>

		<div class="ms-body">
			<div class="ms-page">

				<!-- ── Visit Details ─────────────────────────────────── -->
				<div class="ms-section">
					<div class="ms-section-label"><span>Visit Details</span></div>

					<div class="ms-field">
						<label for="partner_id" class="ms-label">Partner</label>
						<select id="partner_id" class="form-select" v-on:change="getPartnerData" v-model="partner_id">
							<option :value="null">Select a partner…</option>
							<option v-for="partner in partners" v-bind:key="partner.id" :value="partner.id">{{ partner.slug }}</option>
						</select>
						<p class="ms-hint">Partners without remaining seats will not be listed.</p>
					</div>

					<div class="ms-field">
						<label for="scenario_id" class="ms-label">Scenario</label>
						<select id="scenario_id" class="form-select" v-on:change="getScenarioData" v-model="scenario_id" :disabled="!partner_id">
							<option :value="null">Select a scenario…</option>
							<option v-for="scenario in scenarios" v-bind:key="scenario.id" :value="scenario.id">{{ scenario.id }} — {{ scenario.patient_name }}</option>
						</select>
					</div>

					<div class="ms-field">
						<label for="job_number" class="ms-label">Job Number</label>
						<input type="text" id="job_number" class="form-control ms-readonly" disabled v-model="job_number" placeholder="Populated from partner / scenario combination" />
					</div>
				</div>

				<!-- ── Participants ──────────────────────────────────── -->
				<div class="ms-section">
					<div class="ms-section-label"><span>Participants</span></div>

					<!-- Gathered link (only shown when applicable) -->
					<div v-if="has_gathered" class="ms-gathered-box ms-field">
						<p class="ms-gathered-title">
							<i class="fas fa-link me-1"></i>
							Gathered Curriculum Link
							<span class="ms-gathered-optional">(optional)</span>
						</p>
						<select id="gathered_user" class="form-select" v-model="selected_gathered_user" v-on:change="onGatheredUserChange">
							<option :value="null">None — no gathered link</option>
							<option v-for="gu in gathered_users" v-bind:key="gu.gathered_user_id + '-' + gu.gathered_group_id" :value="gu">
								{{ gu.first_name }} {{ gu.last_name }} ({{ gu.email }}) — Group {{ gu.gathered_group_id }}
							</option>
						</select>
					</div>

					<div class="ms-field">
						<label for="p_user_id" class="ms-label">Participant</label>
						<select id="p_user_id" class="form-select" v-model="p_user_id" v-on:change="isSubmitAvailable" :disabled="selected_gathered_user !== null || !scenario_id">
							<option :value="null">Select a participant…</option>
							<option v-for="p_user in p_users" v-bind:key="p_user.id" :value="p_user.id">{{ p_user.first_name }} {{ p_user.last_name }} ({{ p_user.email }})</option>
						</select>
						<p v-if="selected_gathered_user !== null" class="ms-lock-note">
							<i class="fas fa-lock"></i> Locked to gathered selection — choose "None" above to unlock.
						</p>
					</div>

					<div class="ms-field">
						<label for="sp_user_id" class="ms-label">Standardized Patient</label>
						<select id="sp_user_id" class="form-select" v-model="sp_user_id" v-on:change="isSubmitAvailable" :disabled="!scenario_id">
							<option :value="null">Select a standardized patient…</option>
							<option v-for="sp_user in sp_users" v-bind:key="sp_user.id" :value="sp_user.id">{{ sp_user.first_name }} {{ sp_user.last_name }} ({{ sp_user.email }})</option>
						</select>
					</div>
				</div>

				<!-- ── Schedule ──────────────────────────────────────── -->
				<div class="ms-section">
					<div class="ms-section-label"><span>Schedule</span></div>

					<div class="row g-3">
						<div class="col-md-6 ms-field mb-0">
							<label for="date" class="ms-label">Date</label>
							<Datepicker id="date" class="ms-datepicker" :lower-limit="new Date()" placeholder="Select a date…" v-model="date" @update:modelValue="isSubmitAvailable" />
						</div>
						<div class="col-md-6 ms-field mb-0">
							<label for="time" class="ms-label">Time <span class="ms-hint d-inline ms-0">(Eastern Time)</span></label>
							<select id="time" class="form-select" v-model="time" v-on:change="isSubmitAvailable">
								<option :value="null">Select a time…</option>
								<option v-for="time_option in time_options" v-bind:key="time_option.value" :value="time_option.value">{{ time_option.label }}</option>
							</select>
						</div>
					</div>
				</div>

				<!-- ── Footer ────────────────────────────────────────── -->
				<div class="ms-footer">
					<p class="ms-hint text-center mb-3">
						All sessions are scheduled in Eastern Time and marked as confirmed upon creation.
					</p>

					<div v-if="scheduled_url" class="ms-success-banner mb-3">
						<i class="fas fa-check-circle me-2"></i>
						<span>Visit scheduled — </span>
						<a :href="'/#/visit/' + visit_id" target="_blank" class="ms-success-action">
							<i class="fas fa-arrow-up-right-from-square"></i> Open link
						</a>
						<span class="ms-success-divider">|</span>
						<button class="ms-success-action ms-copy-btn" @click="copyUrl">
							<i :class="url_copied ? 'fas fa-check' : 'fas fa-copy'"></i>
							{{ url_copied ? 'Copied!' : 'Copy link' }}
						</button>
					</div>

					<button class="btn btn-primary ms-submit-btn" v-on:click="openConfirmModal" :disabled="!submit_available">
						Schedule Visit
					</button>
				</div>

			</div>
		</div>

		<loading :active="loading" :is-full-page="true" />

		<!-- Confirmation modal -->
		<modal v-model="show_confirm_modal" allow-close>
			<template v-slot:header>Confirm Visit Details</template>
			<table class="table table-bordered mb-2">
				<tbody>
					<tr class="ms-confirm-section"><td colspan="2">Visit</td></tr>
					<tr>
						<th class="ms-confirm-th">Partner</th>
						<td>{{ selected_partner?.slug }}</td>
					</tr>
					<tr>
						<th class="ms-confirm-th">Scenario</th>
						<td>{{ selected_scenario?.id }} &ndash; {{ selected_scenario?.patient_name }}</td>
					</tr>
					<tr>
						<th class="ms-confirm-th">Job Number</th>
						<td>{{ job_number ?? '—' }}</td>
					</tr>
					<tr class="ms-confirm-section"><td colspan="2">People</td></tr>
					<tr>
						<th class="ms-confirm-th">Participant</th>
						<td>
							{{ selected_p_user?.first_name }} {{ selected_p_user?.last_name }}
							<br /><small class="text-muted">{{ selected_p_user?.email }}</small>
						</td>
					</tr>
					<tr>
						<th class="ms-confirm-th">SP</th>
						<td>
							{{ selected_sp_user?.first_name }} {{ selected_sp_user?.last_name }}
							<br /><small class="text-muted">{{ selected_sp_user?.email }}</small>
						</td>
					</tr>
					<tr v-if="selected_gathered_user">
						<th class="ms-confirm-th">Gathered</th>
						<td>{{ selected_gathered_user.first_name }} {{ selected_gathered_user.last_name }} — Group {{ selected_gathered_user.gathered_group_id }}</td>
					</tr>
					<tr class="ms-confirm-section"><td colspan="2">Schedule</td></tr>
					<tr>
						<th class="ms-confirm-th">Date</th>
						<td>{{ formatted_date }}</td>
					</tr>
					<tr>
						<th class="ms-confirm-th">Time (Eastern)</th>
						<td>{{ formatted_time }}</td>
					</tr>
				</tbody>
			</table>
			<p class="ms-hint mb-0">This visit will be marked as confirmed. Please review before continuing.</p>
			<template v-slot:footer>
				<div class="d-flex justify-content-end gap-2 mt-4">
					<button class="btn btn-default" @click="show_confirm_modal = false">Cancel</button>
					<button class="btn btn-primary" @click="submit">Continue</button>
				</div>
			</template>
		</modal>

	</div>
</template>

<script lang="ts">
	import { computed, defineComponent, ref } from "vue";
	import { MutationTypes } from "@/store/modules/ux/mutations";
	import { NotificationIcon, NotificationColor } from "@/interfaces/notification";
    import { store } from '@/store/index';
    import Datepicker from 'vue3-datepicker';
	import Loading from 'vue3-loading-overlay';
	import Modal from "@/components/modal.vue";
	import moment from 'moment';
	import Visit from "@/models/visit";

	export default defineComponent({
		components: { Datepicker, Loading, Modal },
		mounted(){
			this.getPartners();
		},
		setup(){
			const date = ref(null);
			const job_number = ref(null);
			const loading = ref(true);
			const partners = ref([]);
			const partner_id = ref(null);
			const p_users = ref([]);
			const p_user_id = ref(null);
			const scenarios = ref([]);
			const scenario_id = ref(null);
			const scheduled_url = ref(null);
			const show_confirm_modal = ref(false);
			const url_copied = ref(false);
			const visit_id = ref(null);
			const sp_users = ref([]);
			const sp_user_id = ref(null);
			const submit_available = ref(false);
			const time = ref(null);

			// Gathered state
			const has_gathered = ref(false);
			const gathered_users = ref([]);
			const selected_gathered_user = ref<any>(null);
			const spoc_curriculum_sped_id = ref<any>(null);

			const time_options = computed(() => {
				const opts = [];
				for (let i = 0; i < 24; i++) {
					const value_h = (i < 10 ? '0' : '') + i;
					const period = i < 12 ? 'am' : 'pm';
					const display_h = i === 0 ? 12 : i > 12 ? i - 12 : i;
					opts.push({ value: `${value_h}:00:00`, label: `${display_h}:00${period}` });
					opts.push({ value: `${value_h}:30:00`, label: `${display_h}:30${period}` });
				}
				return opts;
			});

			const selected_partner = computed(() => partners.value.find((p:any) => p.id === partner_id.value) ?? null);
			const selected_scenario = computed(() => scenarios.value.find((s:any) => s.id === scenario_id.value) ?? null);
			const selected_p_user = computed(() => p_users.value.find((u:any) => u.id === p_user_id.value) ?? null);
			const selected_sp_user = computed(() => sp_users.value.find((u:any) => u.id === sp_user_id.value) ?? null);
			const formatted_date = computed(() => date.value ? moment(date.value).format('MMMM D, YYYY') : null);
			const formatted_time = computed(() => {
				if (!time.value) return null;
				const opt = time_options.value.find((o:any) => o.value === time.value);
				return opt ? opt.label : time.value;
			});

			const openConfirmModal = () => {
				show_confirm_modal.value = true;
			};

			const onGatheredUserChange = () => {
				if (selected_gathered_user.value) {
					const sped_id = selected_gathered_user.value.sped_user_id;
					const in_list = p_users.value.some((u:any) => u.id === sped_id);
					p_user_id.value = in_list ? sped_id : null;
				} else {
					p_user_id.value = null;
				}
				isSubmitAvailable();
			};

			const getPartnerData = async() => {
				loading.value = true;
				var data:any = {
					partner_id: partner_id.value
				};

				await Visit.msGetPartnerData(data).then((res)=>{
					reset(0);
					scenarios.value = res.data.scenarios;
					loading.value = false;
				},(err)=>{
					loading.value = false;
					store.commit(MutationTypes.addSystemNotification, {title:'Error retrieving partner data.',content:err.response.data.message,icon:NotificationIcon.ERROR,color:NotificationColor.RED});
				});

				loading.value = false;
			}

			const getPartners = async() => {
				await Visit.msGetPartners().then((res)=>{
						partners.value = res.data.partners;
                        loading.value = false;
                    },(err)=>{
                        loading.value = false;
                        store.commit(MutationTypes.addSystemNotification, {title:'Error retrieving partners.',content:err.response.data.message,icon:NotificationIcon.ERROR,color:NotificationColor.RED});
                        console.log(err);
                    });

				loading.value = false;
			}

			const getScenarioData = async() => {
				loading.value = true;
				var data:any = {
					scenario_id: scenario_id.value,
					partner_id: partner_id.value,
				};

				await Visit.msGetScenarioData(data).then((res)=>{
					reset(1);
					sp_users.value = res.data.sp_users;
					p_users.value = res.data.p_users;
					job_number.value = res.data.job_number;
					loading.value = false;
				},(err)=>{
					loading.value = false;
					store.commit(MutationTypes.addSystemNotification, {title:'Error retrieving scenario data.',content:err.response.data.message,icon:NotificationIcon.ERROR,color:NotificationColor.RED});
				});

				// Check for gathered curriculum connection
				await Visit.msGetGatheredData(data).then((res)=>{
					has_gathered.value = res.data.has_gathered;
					gathered_users.value = res.data.has_gathered ? res.data.gathered_users : [];
					spoc_curriculum_sped_id.value = res.data.has_gathered ? res.data.spoc_curriculum_sped_id : null;
				},(err)=>{
					has_gathered.value = false;
					gathered_users.value = [];
					spoc_curriculum_sped_id.value = null;
					const msg = err?.response?.data?.message ?? err?.message ?? 'Unknown error';
					store.commit(MutationTypes.addSystemNotification, {title:'Error loading gathered data.',content:msg,icon:NotificationIcon.ERROR,color:NotificationColor.RED});
				});

				loading.value = false;
			}

			const reset = (depth:number) => {
				if(depth == 0){
					scenario_id.value = null;
					has_gathered.value = false;
					gathered_users.value = [];
					selected_gathered_user.value = null;
					spoc_curriculum_sped_id.value = null;
				}
				job_number.value = null;
				p_user_id.value = null;
				sp_user_id.value = null;
				date.value = null;
				time.value = null;
				submit_available.value = false;
				scheduled_url.value = null;
				visit_id.value = null;
				selected_gathered_user.value = null;
			}

			const isSubmitAvailable = () => {
				submit_available.value = partner_id.value != null && scenario_id.value != null && p_user_id.value != null && sp_user_id.value != null && date.value != null && time.value != null;
			};

			const submit = async() => {
				show_confirm_modal.value = false;
				loading.value = true;
				let date_formatter = moment(date.value);
				let submit_date = date_formatter.format('YYYY-MM-DD');

				var data:any = {
					partner_id: partner_id.value,
					scenario_id: scenario_id.value,
					job_number: job_number.value,
					p_user_id: p_user_id.value,
					sp_user_id: sp_user_id.value,
					date: submit_date,
					time: time.value,
					spoc_curriculum_sped_id: selected_gathered_user.value ? spoc_curriculum_sped_id.value : null,
					gathered_group_id: selected_gathered_user.value ? selected_gathered_user.value.gathered_group_id : null,
					gathered_user_id: selected_gathered_user.value ? selected_gathered_user.value.gathered_user_id : null,
				};

				await Visit.msScheduleVisit(data).then((res) => {
					reset(0);
					partner_id.value = null;
					scheduled_url.value = res.data.url;
					visit_id.value = res.data.visit_id;
					loading.value = false;
					store.commit(MutationTypes.addSystemNotification, {title: 'Visit Scheduled',content:'',icon:NotificationIcon.SUCCESS,color:NotificationColor.GREEN});
				},(err)=>{
					loading.value = false;
					store.commit(MutationTypes.addSystemNotification, {title: 'Error scheduling visit.',content:err.response.data.message,icon:NotificationIcon.ERROR,color:NotificationColor.RED});
				});
			}

			const copyUrl = () => {
				if (!scheduled_url.value) return;
				navigator.clipboard.writeText(scheduled_url.value).then(() => {
					url_copied.value = true;
					setTimeout(() => { url_copied.value = false; }, 2000);
				});
			};

			return {
				copyUrl,
				date,
				formatted_date,
				formatted_time,
				gathered_users,
				getPartnerData,
				getPartners,
				getScenarioData,
				has_gathered,
				isSubmitAvailable,
				job_number,
				loading,
				onGatheredUserChange,
				openConfirmModal,
				partners,
				partner_id,
				scenarios,
				scenario_id,
				p_users,
				p_user_id,
				scheduled_url,
				selected_gathered_user,
				selected_p_user,
				selected_partner,
				selected_scenario,
				selected_sp_user,
				show_confirm_modal,
				sp_users,
				sp_user_id,
				spoc_curriculum_sped_id,
				submit_available,
				time,
				time_options,
				submit,
				url_copied,
				visit_id,
			}
		}
	});
</script>

<style lang="scss">
// All classes are prefixed ms- so scoped is not needed.
// Avoiding :deep() which can cause silent SCSS compile failures.

/* ── Layout ───────────────────────────────────────────────── */
.ms-header {
	background: #122b55;
	padding: 1.75rem 2rem 1.5rem;
	border-radius: calc(0.375rem - 1px) calc(0.375rem - 1px) 0 0;
}
.ms-title {
	font-size: 1.7rem;
	color: #fff;
	margin-bottom: 0.2rem;
}
.ms-subtitle {
	font-size: 1.1rem;
	color: rgba(255, 255, 255, 0.85);
	margin: 0;
}

.ms-body {
	padding: 2rem;
}
.ms-page {
	max-width: 680px;
	margin: 0 auto;
}

/* ── Sections ─────────────────────────────────────────────── */
.ms-section {
	margin-bottom: 2rem;
}
.ms-section-label {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1.25rem;
	font-size: 1.1rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: #6bbaa3;

	&::before,
	&::after {
		content: '';
		flex: 1;
		border-top: 1px solid #e2e8f0;
	}
}

/* ── Fields ───────────────────────────────────────────────── */
.ms-field {
	margin-bottom: 1.125rem;
}
.ms-label {
	display: block !important;
	font-weight: 600;
	margin-bottom: 0.375rem;
}
.ms-hint {
	font-size: 1.1rem;
	color: #718096;
	margin-top: 0.3rem;
	margin-bottom: 0;
}
.ms-readonly {
	background-color: #f7fafc !important;
	color: #718096;
	font-style: italic;
	font-size: 1.3rem !important;
}

/* Normalize all form controls within ms-page */
.ms-page select,
.ms-page input[type="text"],
.ms-page input:not([type]) {
	font-size: 1.3rem;
}

/* ── Datepicker ───────────────────────────────────────────── */
.ms-datepicker {
	width: 100%;

	input {
		width: 100%;
		padding: 0.375rem 0.75rem;
		font-size: 1rem !important;
		border: 1px solid #ced4da;
		border-radius: 0.375rem;
		background-color: #fff;
		line-height: 1.5;
		color: #495057;

		&:focus {
			border-color: #6bbaa3;
			outline: 0;
			box-shadow: 0 0 0 0.2rem rgba(107, 186, 163, 0.25);
		}
	}

}

/* Datepicker popup — top-level rule because the popout is rendered outside
   the datepicker's root element in the DOM (not a descendant of .ms-datepicker) */
.v3dp__popout {
	top: auto !important;
	bottom: 100% !important;
	z-index: 9999 !important;
}

/* ── Gathered box ─────────────────────────────────────────── */
.ms-gathered-box {
	background: #f0f9f6;
	border: 1px solid #b2d8cd;
	border-radius: 6px;
	padding: 1rem 1.125rem;
}
.ms-gathered-title {
	font-weight: 700 !important;
	font-size: 1rem !important;
	color: #122b55 !important;
	margin-bottom: 0.625rem;
}
.ms-gathered-optional {
	font-weight: 400;
	color: #718096;
	margin-left: 0.25rem;
}
.ms-lock-note {
	font-size: 1.1rem;
	color: #718096;
	margin-top: 0.35rem;
	margin-bottom: 0;
	i { margin-right: 0.25rem; }
}

/* ── Footer ───────────────────────────────────────────────── */
.ms-footer {
	border-top: 1px solid #e2e8f0;
	padding-top: 1.5rem;
	margin-top: 0.5rem;
}
.ms-submit-btn {
	width: 100%;
	letter-spacing: 0.02em;
}
.ms-success-banner {
	display: flex;
	align-items: center;
	gap: 0.4rem;
	background: #f0f9f6;
	border: 1px solid #6bbaa3;
	border-radius: 6px;
	padding: 0.75rem 1rem;
	font-size: 1.3rem;
	color: #122b55;
	i { color: #6bbaa3; font-size: 1.1rem; }
}
.ms-success-action {
	color: #122b55;
	font-weight: 600;
	text-decoration: none;
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	font-size: inherit;
	&:hover { text-decoration: underline; color: #122b55; }
}
.ms-success-divider {
	color: #b2d8cd;
	margin: 0 0.1rem;
}
.ms-copy-btn {
	vertical-align: baseline;
}

/* ── Confirmation modal table ─────────────────────────────── */
.ms-confirm-section td {
	background: #f0f9f6 !important;
	color: #6bbaa3 !important;
	font-size: 1.1rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	padding: 0.35rem 0.75rem !important;
}
.ms-confirm-th {
	width: 38%;
	color: #4a5568 !important;
	font-weight: 600 !important;
}
</style>
